import React, { useState } from 'react';
import { 
  Users, 
  Megaphone, 
  CheckCircle, 
  XCircle, 
  Clock,
  TrendingUp,
  MessageSquare,
  Palette
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { alertService } from '../services/alertService';
import SweetAlertDemo from '../components/SweetAlertDemo';

const Dashboard = () => {
  const [showDemo, setShowDemo] = useState(false);

  // Fonction pour afficher les détails d'une statistique
  const handleStatClick = async (stat) => {
    await alertService.info(
      `Détails - ${stat.name}`,
      `Valeur actuelle: ${stat.value}\nÉvolution: ${stat.change}\n\nCliquez sur "Voir plus" pour accéder aux détails complets.`,
      {
        confirmButtonText: 'Voir plus',
        showCancelButton: true,
        cancelButtonText: 'Fermer'
      }
    );
  };

  // Fonction pour démarrer une nouvelle campagne
  const handleNewCampaign = async () => {
    const result = await alertService.confirm(
      'Nouvelle Campagne',
      'Voulez-vous créer une nouvelle campagne SMS ?',
      {
        confirmButtonText: 'Créer',
        cancelButtonText: 'Annuler',
        icon: 'question'
      }
    );

    if (result.isConfirmed) {
      await alertService.success('Redirection...', 'Vous allez être redirigé vers la page de création de campagne');
    }
  };

  // Fonction pour voir les détails d'une campagne
  const handleCampaignClick = async (campaign) => {
    await alertService.custom({
      title: `Campagne: ${campaign.name}`,
      html: `
        <div style="text-align: left;">
          <p><strong>Statut:</strong> ${campaign.status}</p>
          <p><strong>SMS envoyés:</strong> ${campaign.sent}</p>
          <p><strong>SMS livrés:</strong> ${campaign.delivered}</p>
          <p><strong>Date:</strong> ${campaign.date}</p>
          <p><strong>Taux de livraison:</strong> ${campaign.sent > 0 ? ((campaign.delivered / campaign.sent) * 100).toFixed(1) : 0}%</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Modifier',
      cancelButtonText: 'Fermer'
    });
  };

  // Données simulées pour les statistiques
  const stats = [
    { name: 'Total Contacts', value: '2,847', icon: Users, change: '+12%', changeType: 'positive' },
    { name: 'Campagnes Actives', value: '23', icon: Megaphone, change: '+3', changeType: 'positive' },
    { name: 'SMS Envoyés', value: '15,432', icon: MessageSquare, change: '+8.2%', changeType: 'positive' },
    { name: 'Taux de Livraison', value: '98.5%', icon: CheckCircle, change: '+0.3%', changeType: 'positive' },
  ];

  const campaignData = [
    { name: 'Jan', envoyés: 1200, livrés: 1180 },
    { name: 'Fév', envoyés: 1900, livrés: 1870 },
    { name: 'Mar', envoyés: 3000, livrés: 2950 },
    { name: 'Avr', envoyés: 2800, livrés: 2750 },
    { name: 'Mai', envoyés: 1890, livrés: 1860 },
    { name: 'Juin', envoyés: 2390, livrés: 2350 },
  ];

  const statusData = [
    { name: 'Livrés', value: 15432, color: '#10B981' },
    { name: 'En attente', value: 234, color: '#F59E0B' },
    { name: 'Échecs', value: 89, color: '#EF4444' },
  ];

  const recentCampaigns = [
    { id: 1, name: 'Promotion Été 2024', status: 'Terminé', sent: 1250, delivered: 1234, date: '2024-06-15' },
    { id: 2, name: 'Rappel RDV', status: 'En cours', sent: 890, delivered: 876, date: '2024-06-16' },
    { id: 3, name: 'Newsletter Mensuelle', status: 'Planifié', sent: 0, delivered: 0, date: '2024-06-20' },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Terminé': return 'text-green-600 bg-green-100';
      case 'En cours': return 'text-blue-600 bg-blue-100';
      case 'Planifié': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Fonction pour basculer la démonstration
  const toggleDemo = () => {
    setShowDemo(!showDemo);
  };

  if (showDemo) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-gray-100">
              🎨 Démonstration SweetAlert2
            </h1>
            <p className="mt-1 text-sm text-secondary-500 dark:text-gray-400">
              Testez les modales et alertes personnalisées
            </p>
          </div>
          <button
            onClick={toggleDemo}
            className="btn btn-secondary"
          >
            Retour au Dashboard
          </button>
        </div>
        <SweetAlertDemo />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-gray-100">Tableau de bord</h1>
          <p className="mt-1 text-sm text-secondary-500 dark:text-gray-400">
            Vue d'ensemble de votre plateforme SMS Gateway
          </p>
        </div>
        <button
          onClick={toggleDemo}
          className="btn btn-secondary flex items-center"
        >
          <Palette className="h-4 w-4 mr-2" />
          Tester les modales
        </button>
      </div>

      {/* Statistiques principales */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div 
              key={stat.name} 
              className="card cursor-pointer hover:shadow-md transition-shadow duration-200"
              onClick={() => handleStatClick(stat)}
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Icon className="h-8 w-8 text-primary-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-secondary-500 truncate">
                      {stat.name}
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-secondary-900">
                        {stat.value}
                      </div>
                      <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                        stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {stat.change}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Graphique des campagnes */}
        <div className="card">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">
            Évolution des SMS (6 derniers mois)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={campaignData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="envoyés" fill="#3B82F6" name="Envoyés" />
              <Bar dataKey="livrés" fill="#10B981" name="Livrés" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Graphique des statuts */}
        <div className="card">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">
            Statut des SMS
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Campagnes récentes */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-secondary-900 dark:text-gray-100">
            Campagnes récentes
          </h3>
          <button
            onClick={handleNewCampaign}
            className="btn btn-primary text-sm"
          >
            + Nouvelle campagne
          </button>
        </div>
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table className="table">
            <thead>
              <tr>
                <th>Nom de la campagne</th>
                <th>Statut</th>
                <th>Envoyés</th>
                <th>Livrés</th>
                <th>Date</th>
                <th>Taux de livraison</th>
              </tr>
            </thead>
            <tbody>
              {recentCampaigns.map((campaign) => (
                <tr 
                  key={campaign.id} 
                  className="cursor-pointer hover:bg-secondary-50 dark:hover:bg-gray-700 transition-colors duration-200"
                  onClick={() => handleCampaignClick(campaign)}
                >
                  <td className="font-medium text-secondary-900 dark:text-gray-100">
                    {campaign.name}
                  </td>
                  <td>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(campaign.status)}`}>
                      {campaign.status}
                    </span>
                  </td>
                  <td className="text-secondary-900 dark:text-gray-100">{campaign.sent.toLocaleString()}</td>
                  <td className="text-secondary-900 dark:text-gray-100">{campaign.delivered.toLocaleString()}</td>
                  <td className="text-secondary-900 dark:text-gray-100">{new Date(campaign.date).toLocaleDateString('fr-FR')}</td>
                  <td className="text-secondary-900 dark:text-gray-100">
                    {campaign.sent > 0 ? ((campaign.delivered / campaign.sent) * 100).toFixed(1) : 0}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
